import { useSetAtom } from 'jotai';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSvg } from '@/lib/v2/components';
import { CardAction } from '@/lib/v2/examples/CardAction';
import { CardActionProps } from '@/lib/v2/examples/CardAction/CardAction';

import { IRestricted } from '@/src/presentation/types/types';

import { atomActionTypeName } from '@/modules/CampaignsModule/atoms/createActionsAtom';
import {
  APIEmailIcon,
  APISmsIcon,
  SimpleEmailIcon,
  SimpleSmsIcon,
  TriggerEmailIcon,
} from '@/modules/CampaignsModule/images/icons';
import { AttachmentIcon } from '@/modules/CampaignsModule/images/icons/AttachmentIcon';
import { ActionTypeName } from '@/modules/CampaignsModule/interfaces/Campaigns';

interface AllsTabPaneContainerProps {
  filterBy?: 'all' | 'email' | 'sms';
  onClose?: () => void;
  onOpenCreateActionNameModal?: () => void;
  restrictions?: IRestricted | null;
  onLabelClick?: (action: ActionTypeName) => void;
}

const AllsTabPaneContainer = ({
  filterBy = 'all',
  onClose,
  onOpenCreateActionNameModal,
  restrictions,
  onLabelClick,
}: AllsTabPaneContainerProps) => {
  const { t } = useTranslation();

  const setActionTypeName = useSetAtom(atomActionTypeName);

  const handleOpenCreateActionNameModal = useCallback(
    (actionType: ActionTypeName) => {
      setActionTypeName(actionType);
      onOpenCreateActionNameModal?.();
      onClose?.();
    },
    [onClose, onOpenCreateActionNameModal, setActionTypeName]
  );

  const cardActionsList = useMemo<CardActionProps[]>(
    () => [
      {
        description: t('ALL_CARDS_ACTIONS.simpleEmail.description'),
        title: t('ALL_CARDS_ACTIONS.simpleEmail.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'simple-email',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<SimpleEmailIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleOpenCreateActionNameModal('email'),
      },
      {
        description: t('ALL_CARDS_ACTIONS.triggerEmail.description'),
        title: t('ALL_CARDS_ACTIONS.triggerEmail.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'trigger-email',
        badgeText: t('ALL_CARDS_ACTIONS.triggerEmail.badgeText'),
        badgeColor: 'sleepy',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<TriggerEmailIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleOpenCreateActionNameModal('email-trigger'),
      },
      {
        description: t('ALL_CARDS_ACTIONS.simpleSms.description'),
        title: t('ALL_CARDS_ACTIONS.simpleSms.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        withLabel:
          !restrictions?.allAllowed && restrictions?.elementsBlocked?.sendSms
            ? 'Upgrade plan'
            : undefined,
        id: 'simple-sms',
        disabled: !restrictions?.allAllowed && restrictions?.elementsBlocked?.sendSms,
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<SimpleSmsIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onLabelClick: () => onLabelClick && onLabelClick('sms'),
        onClickAction: () => handleOpenCreateActionNameModal('sms'),
      },
      {
        description: t('ALL_CARDS_ACTIONS.emailAttachment.description'),
        title: t('ALL_CARDS_ACTIONS.emailAttachment.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'attachments-email',
        withLabel:
          !restrictions?.allAllowed && restrictions?.elementsBlocked?.sendWithAttachment
            ? 'Upgrade plan'
            : undefined,
        disabled: !restrictions?.allAllowed && restrictions?.elementsBlocked?.sendWithAttachment,
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<AttachmentIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onLabelClick: () => onLabelClick && onLabelClick('email-attachment'),
        onClickAction: () => handleOpenCreateActionNameModal('email-attachment'),
      },
      {
        description: t('ALL_CARDS_ACTIONS.apiEmail.description'),
        title: t('ALL_CARDS_ACTIONS.apiEmail.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'email-api',
        badgeText: t('ALL_CARDS_ACTIONS.triggerEmail.badgeText'),
        badgeColor: 'sleepy',
        withLabel:
          !restrictions?.allAllowed && restrictions?.elementsBlocked?.mailByApi
            ? 'Upgrade plan'
            : undefined,
        disabled: !restrictions?.allAllowed && restrictions?.elementsBlocked?.mailByApi,
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<APIEmailIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onLabelClick: () => onLabelClick && onLabelClick('mail-api'),
        onClickAction: () => handleOpenCreateActionNameModal('mail-api'),
      },
      {
        description: t('ALL_CARDS_ACTIONS.apiSms.description'),
        title: t('ALL_CARDS_ACTIONS.apiSms.title'),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'sms-api',
        badgeText: t('ALL_CARDS_ACTIONS.triggerEmail.badgeText'),
        badgeColor: 'sleepy',
        withLabel:
          !restrictions?.allAllowed && restrictions?.elementsBlocked?.smsApi
            ? 'Upgrade plan'
            : undefined,
        disabled: !restrictions?.allAllowed && restrictions?.elementsBlocked?.smsApi,
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<APISmsIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onLabelClick: () => onLabelClick && onLabelClick('sms-api'),
        onClickAction: () => handleOpenCreateActionNameModal('sms-api'),
      },
    ],
    [handleOpenCreateActionNameModal, onLabelClick, restrictions, t]
  );

  const filterCardActions = (filterParam: AllsTabPaneContainerProps['filterBy']) => {
    if (!filterParam || filterParam === 'all') {
      return cardActionsList;
    }

    const filteredActions = cardActionsList.filter((action) => {
      return action?.id && action?.id.toLowerCase().includes(filterParam.toLowerCase());
    });

    return filteredActions;
  };

  const createActionsList = filterCardActions(filterBy);

  return (
    <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {createActionsList.map((cardAction) => {
        return <CardAction key={cardAction.id} {...cardAction} justifyActionButton="end" />;
      })}
    </div>
  );
};

export default memo(AllsTabPaneContainer);
